import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { Icon, IconButton } from '@malash-office/ui-components';
import { ChatMessage } from '../hooks/useAgentChat';
import { TurnRating } from '@/api/agent/feedback';
import ChatActionCard from './ChatActionCard';

interface ChatMessageListProps {
  messages: ChatMessage[];
  activeTool: string | null;
  isStreaming: boolean;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  onRate: (seq: number, rating: TurnRating) => void;
}

/**
 * 👍/👎 under a finished answer.
 *
 * The cheapest way to turn a bad answer into something reviewable. Every wrong answer this chat
 * has produced was found by a person reading it and nothing captured that — the conversation had
 * to be reconstructed from a screenshot. One click now files the whole conversation, with the
 * tools and arguments behind it, for review.
 *
 * Shown only once a turn is complete: rating a half-streamed sentence rates nothing.
 */
const RatingControls = ({
  seq,
  rating,
  onRate
}: {
  seq: number;
  rating: TurnRating | null | undefined;
  onRate: (seq: number, rating: TurnRating) => void;
}) => (
  <Box sx={{ display: 'flex', gap: 0.25, alignSelf: 'flex-end', mt: -0.75, opacity: 0.75 }}>
    <IconButton
      variant="text"
      size="small"
      aria-label="תשובה טובה"
      onClick={() => onRate(seq, 'good')}
    >
      <Icon icon={rating === 'good' ? 'thumbUpFilled' : 'thumbUp'} size="small" color="inherit" />
    </IconButton>
    <IconButton
      variant="text"
      size="small"
      aria-label="תשובה שגויה"
      onClick={() => onRate(seq, 'bad')}
    >
      <Icon icon={rating === 'bad' ? 'thumbDownFilled' : 'thumbDown'} size="small" color="inherit" />
    </IconButton>
  </Box>
);

/**
 * Small secondary text is a styled `Box`, not the design system's `Typography`.
 *
 * That component's API is closed to `variant` / `color` / `children`, and its variants stop at
 * `body` — there is no caption tier. Passing `variant="caption"` (as some existing screens do)
 * falls through its lookup to `undefined` and MUI renders body1, so the text is not actually
 * small. A `Box` says what it means.
 */
const Muted = ({ children, align }: { children: React.ReactNode; align: 'flex-start' | 'flex-end' }) => (
  <Box sx={{ alignSelf: align, fontSize: 12, color: 'text.secondary' }}>{children}</Box>
);

export const ChatMessageList = ({
  messages,
  activeTool,
  isStreaming,
  onConfirm,
  onReject,
  onRate
}: ChatMessageListProps) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Follows the stream: depends on the messages themselves, not just their count, so a long
  // answer keeps its newest line in view instead of pinning to the first chunk.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, activeTool]);

  return (
    <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {messages.map((message, index) => {
        if (message.action) {
          return (
            <ChatActionCard
              key={message.action.id}
              action={message.action}
              onConfirm={onConfirm}
              onReject={onReject}
            />
          );
        }

        const isUser = message.role === 'user';
        // The last bubble is still being written while the turn streams.
        const isComplete = !isStreaming || index < messages.length - 1;
        const canRate = !isUser && message.seq !== undefined && isComplete && Boolean(message.content);

        return (
          <Box
            key={`wrap-${message.role}-${index}`}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
          <Box
            // Index is a safe key here: messages are only appended, and the last one is mutated
            // in place as text streams in.
            key={`${message.role}-${index}`}
            sx={{
              alignSelf: isUser ? 'flex-start' : 'flex-end',
              maxWidth: '85%',
              px: 1.5,
              py: 1,
              borderRadius: '14px',
              fontSize: 14,
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              backgroundColor: isUser ? 'primary.main' : 'grey.100',
              color: isUser ? 'common.white' : 'text.primary'
            }}
          >
            {message.content}
          </Box>
          {canRate && (
            <RatingControls seq={message.seq as number} rating={message.rating} onRate={onRate} />
          )}
          </Box>
        );
      })}

      {/* Tool narration. Without it, an eight-step turn is indistinguishable from a hang. */}
      {activeTool && <Muted align="flex-end">{activeTool}…</Muted>}

      {isStreaming && !activeTool && messages[messages.length - 1]?.role === 'user' && (
        <Muted align="flex-end">חושב…</Muted>
      )}

      <Box ref={bottomRef} />
    </Box>
  );
};

export default ChatMessageList;
